import type { ReactElement } from 'react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import type { PontoFinanceiro } from './types'

import { EmptyState } from '@/components/ui/empty-state'
import { moedaCompacta } from '@/lib/formato'

const MESES_CURTOS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
]

export function rotuloMes(mes: string): string {
  const [, m] = mes.split('-')
  return MESES_CURTOS[Number(m) - 1] ?? mes
}

/** Formatadores fora do JSX: dentro do recharts não rodam em jsdom (sem dimensão). */
export function formatarTick(valor: number): string {
  return moedaCompacta(valor)
}

export function formatarValor(valor: unknown, nome: unknown): [string, string] {
  return [moedaCompacta(Number(valor)), String(nome)]
}

export interface GraficoFinanceiroProps {
  pontos: PontoFinanceiro[]
}

/** Receita e EBITDA por mês — barras lado a lado. */
export function GraficoFinanceiro({ pontos }: GraficoFinanceiroProps): ReactElement {
  if (pontos.length === 0) {
    return <EmptyState size="sm" title="Sem histórico no período." />
  }

  const dados = pontos.map((p) => ({ ...p, rotulo: rotuloMes(p.mes) }))

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={dados} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis
            dataKey="rotulo"
            stroke="hsl(var(--muted-foreground))"
            fontSize={12}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            stroke="hsl(var(--muted-foreground))"
            fontSize={12}
            tickLine={false}
            axisLine={false}
            width={70}
            tickFormatter={formatarTick}
          />
          <Tooltip
            contentStyle={{
              background: 'hsl(var(--popover))',
              border: '1px solid hsl(var(--border))',
              borderRadius: 8,
              color: 'hsl(var(--popover-foreground))',
            }}
            formatter={formatarValor}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="receita" name="Receita" fill="#e9c46a" radius={[4, 4, 0, 0]} />
          <Bar dataKey="ebitda" name="EBITDA" fill="#5fe3c0" radius={[4, 4, 0, 0]} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}
